import React from 'react'
import Pt from 'prop-types'
import { Button, Panel, Radio } from 'react-bootstrap'
import { IndexLinkContainer } from 'react-router-bootstrap'
import Ipt from 'react-immutable-proptypes'
import cx from 'classnames'

import Icon from 'internal-components/src/components/Icon'
import Months from 'internal-components/src/components/Time/Months'
import {
  OfferRecord,
  OfferFilterDataRecord,
  OfferFilterRecord,
} from 'internal-components/src/records'

export default function Filter({
  addressSupplied = false,
  currentOffer,
  filterData,
  filter,
  hideAddressButton = false,
  qualificationResults,
  updateFilter,
}) {
  function zeropad(string, length) {
    while (string.length < length) { string = '0' + string; }
    return string;
  }

  let filters
  if (filter.productId) {
    const product = filterData.products.find(p => p.id === filter.productId)

    if (product) {
      const speedLimitReason = qualificationResults.getIn([filter.productId, 'restrictions', 'speedLimitReason'], null);

      const sortedSpeeds = filterData.speedsWithAttributes.filter(s =>
          !(s && s.attributes && s.attributes.includes('no-fibre-only') && speedLimitReason !== 'no-fibre')
        )
        .map(s => s.speed)
        .sortBy(s => s.replace(/\d+/g, m => zeropad(m, 50)));

      let availableSpeeds;

      // Don't show speeds if they exceed maxDown and maxUp in the relevant qualification results
      const maxDown = qualificationResults.getIn([filter.productId, 'restrictions', 'maxDown'], null);
      const maxUp   = qualificationResults.getIn([filter.productId, 'restrictions', 'maxUp'  ], null);

      availableSpeeds = sortedSpeeds;

      if (maxDown !== null || maxUp !== null) {
        availableSpeeds = availableSpeeds.filter(s => {
          const m = s.match(/\d+/g);
          if (m && m.length === 2) {
            const down = parseFloat(m[0]), up = parseFloat(m[1]);
            if (maxDown !== null && down > maxDown) { return false; }
            if (maxUp   !== null && up   > maxUp  ) { return false; }
          }
          return true;
        });
      } 

      filters = (
        <div>
          {product.type !== 'HOSTING' &&
            availableSpeeds.size > 0 && (
              <div className="speeds">
                <h2>Speed</h2>

                <ul>
                  {availableSpeeds.map(speed => (
                    <li key={speed} className="speed">
                      <Radio
                        checked={filter.speed === speed}
                        className={
                          currentOffer && currentOffer.speed === speed
                            ? 'current-offer'
                            : ''
                        }
                        onChange={() => updateFilter('speed', speed)}
                        value={speed || ''}
                      >
                        {speed}
                      </Radio>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {filterData.contracts.size > 0 && (
            <div className="contracts">
              <h2>Contract Length</h2>

              <ul>
                {filterData.contracts.map(contract => (
                  <li key={contract.asMonths()} className="contract">
                    <Radio
                      checked={
                        filter.contract &&
                        filter.contract.asMonths() === contract.asMonths()
                      }
                      className={
                        currentOffer &&
                        (currentOffer.contractLength ? currentOffer.contractLength.asMonths() : 0) ===
                          contract.asMonths()
                          ? 'current-offer'
                          : ''
                      }
                      onChange={() => updateFilter('contract', contract)}
                      value={contract.asMonths()}
                    >
                      {contract.asMonths() ? <Months duration={contract} /> : 'No Contract'}
                    </Radio>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {filterData.periods.size > 0 && (
            <div className="periods">
              <h2>Billing Period</h2>

              <ul>
                {filterData.periods.map(period => (
                  <li key={period.asMonths()} className="period">
                    <Radio
                      checked={filter.period && filter.period.asMonths() === period.asMonths()}
                      className={
                        currentOffer &&
                        currentOffer.period.asMonths() === period.asMonths()
                          ? 'current-offer'
                          : ''
                      }
                      onChange={() => updateFilter('period', period)}
                      value={period.asMonths()}
                    >
                      <Months duration={period} />
                    </Radio>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )
    }
  }

  return (
    <section id="offer-filter">
      <div id="product-type">
        <h2>Service Type</h2>

        <ul className="product-type">
          {filterData.products.map(product => {
            const qualified = qualificationResults.getIn([product.id, 'available'], false)

            return (
              <li
                key={product.id}
                className={cx('product', {
                  qualified: qualified !== false,
                  [`qualified-${qualified}`]: qualified !== false,
                })}
              >
                <Radio
                  checked={filter.productId === product.id}
                  className={
                    currentOffer && currentOffer.product === product.id
                      ? 'current-offer'
                      : ''
                  }
                  onChange={() => updateFilter('productId', product.id)}
                  value={product.name}
                >
                  {product.name}
                  {qualified > 0 && (
                    <span style={{ color: 'green' }}>
                      <br />
                      Qualification: Pass
                    </span>
                  )}
                </Radio>
              </li>
            )
          })}
        </ul>

        {!addressSupplied && (
          <Panel
            bsStyle="warning"
            className="address-warning"
            style={{ marginTop: '0.5em' }}
          >
            <Panel.Body>
              Without an address, we can only present virtual services.
              {!hideAddressButton && (
                <IndexLinkContainer to="/">
                  <Button
                    bsSize="small"
                    bsStyle="warning"
                    style={{ marginTop: '0.5em' }}
                  >
                    <Icon name="map-marker" /> Enter an address
                  </Button>
                </IndexLinkContainer>
              )}
            </Panel.Body>
          </Panel>
        )}

        <ul className="product-type">
          {filterData.disabledProducts.map(product => (
            <li key={product.id} className="product disabled">
              <Radio
                checked={filter.productId === product.id}
                className={
                  currentOffer && currentOffer.product === product.id
                    ? 'current-offer'
                    : ''
                }
                disabled
                value={product.name}
              >
                {product.name}
              </Radio>
            </li>
          ))}
        </ul>
      </div>

      {filters}
    </section>
  )
}

Filter.propTypes = {
  addressSupplied: Pt.bool,
  currentOffer: Pt.instanceOf(OfferRecord),
  filterData: Pt.instanceOf(OfferFilterDataRecord).isRequired,
  filter: Pt.instanceOf(OfferFilterRecord).isRequired,
  hideAddressButton: Pt.bool,
  qualificationResults: Ipt.map.isRequired,
  updateFilter: Pt.func.isRequired,
}



// WEBPACK FOOTER //
// ./src/components/OfferSelector/Filter.jsx